import { useState } from 'react'

export default function WorkforcePlannersHomepage() {
  const [submitted, setSubmitted] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  const questions = [
    'Do you have enough staff?',
    'Are you seeing high available or idle time?',
    'Do you have overtime costs?',
    'Do you experience challenges with staffing or coverage?',
    'Does your team experience challenges during peak hours or seasons?',
    'Does your team share positive feedback about work-life balance?',
    'Does your team experience employee burnout?',
    'Are your schedules published on time?',
    'Are PTOs approved in advance?',
    'Are you meeting your performance goals?',
    'Do you have visibility into how workforce decisions affect revenue?',
    'Do you need support optimizing your existing tools or building customized solutions?'
  ]

  const servicesTop = [
    {
      title: 'Forecasting & Capacity Planning',
      text: 'Forecasting designed to support changing business climates and workforce realities.'
    },
    {
      title: 'Scheduling Optimization',
      text: 'Custom scheduling frameworks designed to balance efficiency, service performance, and employee experience.'
    },
    {
      title: 'Workforce Analytics & Reporting',
      text: 'Workforce reporting designed to turn data into clear decisions, performance visibility, and actionable insights.'
    }
  ]

  const servicesBottom = [
    {
      title: 'Custom Workforce Tools',
      text: 'Purpose-built workforce solutions to evaluate new technology, maximize existing investments, or build flexible alternatives without unnecessary spend.'
    },
    {
      title: 'Workforce Concierge',
      text: 'White-glove workforce guidance designed for long-term operational support and optimization.'
    },
    {
      title: 'Other Services',
      text: 'Performance strategy, BI, customer experience, client strategy, and account management support for revenue goals.'
    }
  ]

  return (
    <main className="min-h-screen overflow-hidden bg-[#F7F5F2] text-[#181818]">
      {/* HEADER */}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#111111]/85 text-[#F7F5F2] backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-6 lg:px-8">
          <a href="#top" className="font-serif text-[15px] tracking-[0.18em] text-[#F7F5F2] sm:text-[16px]">
            THE WORKFORCE PLANNERS
          </a>

          <nav className="hidden items-center gap-6 text-[11px] uppercase tracking-[0.15em] text-white/75 md:flex">
            <a href="#seasonal-plan" className="transition hover:text-white">Seasonal / 2027</a>
            <a href="#solutions" className="transition hover:text-white">Solutions</a>
            <a href="#impact" className="transition hover:text-white">Impact</a>
            <a href="#philosophy" className="transition hover:text-white">Philosophy</a>
            <a href="#about" className="transition hover:text-white">About</a>
            <a href="#schedule" className="border border-[#8A8966]/70 px-4 py-2 text-[#F7F5F2] transition hover:border-[#8A8966] hover:bg-white/5">
              Consultation
            </a>
          </nav>

          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="text-[11px] uppercase tracking-[0.18em] text-white/80 md:hidden"
            aria-expanded={menuOpen}
            aria-label="Toggle navigation menu"
          >
            {menuOpen ? 'Close' : 'Menu'}
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-white/10 bg-[#111111] px-5 py-5 md:hidden">
            <nav className="flex flex-col gap-4 text-[12px] uppercase tracking-[0.16em] text-white/80">
              <a href="#seasonal-plan" onClick={() => setMenuOpen(false)}>Seasonal / 2027</a>
              <a href="#solutions" onClick={() => setMenuOpen(false)}>Solutions</a>
              <a href="#impact" onClick={() => setMenuOpen(false)}>Impact</a>
              <a href="#philosophy" onClick={() => setMenuOpen(false)}>Philosophy</a>
              <a href="#about" onClick={() => setMenuOpen(false)}>About</a>
              <a href="#schedule" onClick={() => setMenuOpen(false)} className="text-[#C9C8A4]">Consultation</a>
            </nav>
          </div>
        )}
      </header>
      {/* HERO */}
      <section id="top" className="relative min-h-screen overflow-hidden bg-[#111111] text-[#F7F5F2]">
        <div className="relative z-20 flex min-h-screen items-center justify-center px-6 text-center">
          <div className="mb-16 flex flex-col items-center">
            <div className="logo-mark text-center uppercase leading-none">
              <div className="mb-1 flex items-center justify-center gap-6">
                <div className="h-px w-[64px] bg-[#8A8966] md:w-[104px]" />
                <div className="text-[13px] tracking-[0.28em] text-[#F3EEE3] md:text-[18px]">
                  THE
                </div>
                <div className="h-px w-[64px] bg-[#8A8966] md:w-[104px]" />
              </div>

              <div className="logo-workforce text-[2rem] tracking-[0.12em] text-[#FAF8F3] md:text-[3.05rem] lg:text-[3.9rem]">
                WORKFORCE
              </div>

              <div className="logo-planners text-[1.55rem] tracking-[0.30em] text-[#7B8465] md:text-[2.3rem] lg:text-[3rem]">
                PLANNERS
              </div>

              <div className="mb-6 mt-4 flex items-center justify-center gap-6">
                <div className="h-px w-[96px] bg-[#8A8966] md:w-[142px]" />
                <div className="h-[7px] w-[7px] rotate-45 bg-[#8A8966]" />
                <div className="h-px w-[96px] bg-[#8A8966] md:w-[142px]" />
              </div>

              <div className="logo-descriptor text-[10px] uppercase tracking-[0.36em] text-[#E7E2D9] md:text-[13px]">
                Boutique Workforce Consulting
              </div>
            </div>

            <h1 className="mb-6 mt-12 font-serif text-[1.6rem] leading-[1.04] md:text-[2.7rem] lg:text-[3.25rem]">
              Workforce Strategy,
              <span className="block italic text-[#7B9486]">Refined.</span>
            </h1>

            <div className="mt-4 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.24em] text-[#E7E2D9] md:text-[15px]">
              <span className="animate-wordOne">Curate.</span>
              <span className="animate-wordTwo">Bespoke.</span>
              <span className="animate-wordThree">Concierge.</span>
            </div>

            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a
                href="#schedule"
                className="rounded-full bg-[#4E6A5E] px-8 py-4 text-sm uppercase text-white"
              >
                Schedule a Consultation
              </a>

              <a
                href="#solutions"
                className="rounded-full border border-white/30 px-8 py-4 text-sm uppercase text-white"
              >
                Explore Solutions
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* HOLIDAY + 2027 CTA */}
      <section
        id="seasonal-plan"
        className="scroll-mt-24 bg-[#E9E4DA] px-6 py-20 text-center text-[#181818] md:py-24"
      >
        <div className="mx-auto max-w-4xl">
          <div className="mb-5 text-xs uppercase tracking-[0.32em] text-[#8A8966]">
            Holiday + 2027 Workforce Planning
          </div>

          <h2 className="font-serif text-[2.65rem] leading-[1.05] text-[#4E6A5E] md:text-[4.5rem]">
            Are you ready for the holiday season and 2027?
          </h2>

          <p className="mx-auto mt-7 max-w-3xl text-[17px] leading-8 text-black/75 md:text-[19px]">
            For any last-minute holiday support or 2027 workforce management needs, drop us a message for a complimentary assessment.
          </p>

          <div className="mt-10 flex items-center justify-center gap-3" aria-hidden="true">
            <div className="h-px w-12 bg-[#8A8966]" />
            <div className="h-[8px] w-[8px] rotate-45 bg-[#8A8966]" />
            <div className="h-px w-12 bg-[#8A8966]" />
          </div>

          <a
            href="#schedule"
            className="mt-10 inline-flex items-center justify-center rounded-full bg-[#4E6A5E] px-8 py-4 text-sm uppercase tracking-[0.12em] text-white transition hover:bg-[#3F574C]"
          >
            Start a Conversation
          </a>
        </div>
      </section>

      {/* QUESTIONS */}
      <section className="bg-[#F7F5F2] px-6 py-24 md:py-32">
        <div className="mx-auto grid max-w-[1500px] gap-8 lg:grid-cols-[260px_1fr]">
          <h2 className="font-serif text-[2.4rem] md:text-[3.6rem]">
            Questions Worth Asking.
          </h2>

          <div className="relative rounded-[28px] border border-black/10 bg-white p-8 md:p-12">
            <div className="pointer-events-none absolute -right-20 -top-16 font-serif text-[12rem] text-[#4E6A5E] opacity-[0.03]">
              TWP
            </div>

            <div className="relative z-10 mb-5">
              <h3 className="mb-4 font-serif text-[2rem] md:text-[3rem]">
                Workforce Assessment
              </h3>
            </div>

            <div className="relative z-10 space-y-2 text-[15px] text-black/80 md:text-[17px]">
              {questions.map((question) => (
                <div key={question} className="py-3">
                  {question}
                </div>
              ))}

              <div className="pt-10 text-center">
                <p className="mb-8 text-black/60">
                  Ready to explore solutions tailored to your workforce environment?
                </p>

                <a
                  href="#schedule"
                  className="inline-flex w-full max-w-[320px] items-center justify-center rounded-full bg-[#4E6A5E] px-6 py-4 text-center text-xs uppercase leading-5 tracking-[0.12em] text-white sm:w-auto sm:max-w-none sm:px-10 sm:py-5 sm:text-sm"
                >
                  Schedule a Complimentary Consultation
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="solutions" className="scroll-mt-24 bg-[#111111] px-6 py-24 text-[#F7F5F2]">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-px md:grid-cols-3">
            {servicesTop.map((service, index) => (
              <div
                key={service.title}
                className="flex min-h-[320px] flex-col justify-between bg-[#111111] p-10 transition-all hover:bg-[#171717]"
              >
                <div className="mb-6 text-[#7B9486]">0{index + 1}</div>
                <div>
                  <h3 className="mb-4 font-serif text-2xl">{service.title}</h3>
                  <p className="leading-7 text-[#E7E2D9] opacity-90">
                    {service.text}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-px grid grid-cols-1 gap-px md:grid-cols-3">
            {servicesBottom.map((service, index) => (
              <div
                key={service.title}
                className="flex min-h-[320px] flex-col justify-between bg-[#111111] p-10 transition-all hover:bg-[#171717]"
              >
                <div className="mb-6 text-[#7B9486]">0{index + 4}</div>
                <div>
                  <h3 className="mb-4 font-serif text-2xl">{service.title}</h3>
                  <p className="leading-7 text-[#E7E2D9] opacity-90">
                    {service.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SELECTED IMPACT */}
      <section id="impact" className="scroll-mt-24 border-y border-black/10 bg-[#F7F5F2] px-5 py-16 text-[#181818] sm:px-6 md:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="mb-10 text-center md:mb-12">
            <div className="mb-4 text-[11px] uppercase tracking-[0.30em] text-[#4E6A5E] sm:text-xs sm:tracking-[0.32em]">
              Selected Impact
            </div>
            <h2 className="mx-auto max-w-5xl font-serif text-[2.35rem] leading-[1.02] tracking-[-0.025em] sm:text-[3rem] md:text-[4rem]">
              Experience measured in outcomes.
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-px border border-black/10 bg-black/10 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex min-h-[205px] flex-col items-center justify-center bg-[#F7F5F2] px-5 py-9 text-center sm:min-h-[235px] lg:min-h-[270px]">
              <div className="font-serif text-[3.4rem] leading-none text-[#4E6A5E] sm:text-[3.7rem] xl:text-[4.2rem]">
                92%
              </div>
              <div className="mt-5 max-w-[250px] text-[11px] uppercase leading-5 tracking-[0.15em] text-black/65 sm:text-xs xl:text-sm">
                Productivity Achieved
              </div>
            </div>

            <div className="flex min-h-[205px] flex-col items-center justify-center bg-[#F7F5F2] px-5 py-9 text-center sm:min-h-[235px] lg:min-h-[270px]">
              <div className="max-w-[290px] font-serif text-[2.25rem] leading-[0.98] tracking-[-0.025em] text-[#4E6A5E] sm:text-[2.45rem] lg:max-w-[260px] lg:text-[2.15rem] xl:max-w-[300px] xl:text-[2.5rem]">
                <span className="block">Red Zone</span>
                <span className="block">Transformation</span>
              </div>
              <div className="mt-6 max-w-[290px] text-[11px] uppercase leading-5 tracking-[0.14em] text-black/65 sm:text-xs lg:max-w-[250px] xl:max-w-[290px]">
                From Critical Performance to Exceeding Expectations
              </div>
            </div>

            <div className="flex min-h-[205px] flex-col items-center justify-center bg-[#F7F5F2] px-5 py-9 text-center sm:min-h-[235px] lg:min-h-[270px]">
              <div className="font-serif text-[3.4rem] leading-none text-[#4E6A5E] sm:text-[3.7rem] xl:text-[4.2rem]">
                $1M+
              </div>
              <div className="mt-5 max-w-[250px] text-[11px] uppercase leading-5 tracking-[0.15em] text-black/65 sm:text-xs xl:text-sm">
                Workforce Cost Savings
              </div>
            </div>

            <div className="flex min-h-[205px] flex-col items-center justify-center bg-[#F7F5F2] px-5 py-9 text-center sm:min-h-[235px] lg:min-h-[270px]">
              <div className="font-serif text-[3.15rem] leading-[0.95] text-[#4E6A5E] sm:text-[3.5rem] xl:text-[4rem]">
                <span className="block">20+</span>
                <span className="block">Years</span>
              </div>
              <div className="mt-5 max-w-[250px] text-[11px] uppercase leading-5 tracking-[0.15em] text-black/65 sm:text-xs xl:text-sm">
                Workforce Management Expertise
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BOUTIQUE EXPERTISE */}
      <section className="bg-[#E9E4DA] px-6 py-20 text-[#181818] md:py-28">
        <div className="mx-auto max-w-7xl">
          <h2 className="mx-auto max-w-5xl text-center font-serif text-[2.9rem] leading-[1] tracking-[-0.035em] md:text-[5.2rem]">
            Boutique Expertise.
            <br />
            Enterprise Impact.
          </h2>

          <div className="mt-14 grid gap-x-14 gap-y-0 md:grid-cols-2">
            {[
              {
                title: 'Curated',
                text: 'Thoughtfully designed workforce solutions tailored to operational structure, workforce realities, and business priorities.'
              },
              {
                title: 'Operationally Practical',
                text: 'We streamline workflows, reduce redundancy, and strengthen inefficient processes.'
              },
              {
                title: 'Technology',
                text: 'We help you evaluate new options, maximize existing investments, and choose solutions that fit your business.'
              },
              {
                title: 'Concierge',
                text: 'White-glove workforce guidance designed to support implementation, operational transition, workforce systems, and ongoing optimization long after deployment.'
              }
            ].map((item) => (
              <div key={item.title} className="border-t border-black/15 py-7 md:py-9">
                <h3 className="font-serif text-[1.8rem] leading-tight md:text-[2.15rem]">
                  {item.title}
                </h3>
                <p className="mt-5 max-w-xl text-[16px] leading-8 text-black/75 md:text-[18px]">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

                        {/* OUR PHILOSOPHY */}
      <section id="philosophy" className="scroll-mt-24 relative flex min-h-[560px] items-center overflow-hidden bg-[#111111] px-6 py-24 text-[#F7F5F2] md:min-h-[620px] md:py-32">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(123,148,134,0.28),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(78,106,94,0.30),transparent_42%)]" />

        <div className="relative z-10 mx-auto w-full max-w-7xl">
          <div className="mb-6 text-xs uppercase tracking-[0.32em] text-[#7B9486]">
            Our Philosophy
          </div>

          <h2 className="max-w-5xl font-serif text-[2.65rem] leading-[1.05] md:text-[5rem]">
            Plan. Prepare.
            <span className="block italic text-[#7B9486]">
              Perform. Perfect.
            </span>
          </h2>

          <div className="mt-12 h-px w-20 bg-[#8A8966]" />

          <div className="mt-10 max-w-3xl">
            <p className="text-[17px] leading-8 text-[#E7E2D9]/90 md:text-[19px]">
              Carefully curated workforce solutions aligned to business needs, priorities, and available resources.
            </p>
            <p className="mt-5 text-[17px] leading-8 text-[#E7E2D9]/90 md:text-[19px]">
              We tailor focused, practical, and cost-conscious engagements — without unnecessary layers or consulting spend.
            </p>
          </div>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section id="about" className="scroll-mt-24 bg-[#F7F5F2] px-6 py-24 text-[#181818] md:py-32">
        <div className="mx-auto max-w-7xl">
          <div className="mb-6 text-xs uppercase tracking-[0.32em] text-[#4E6A5E]">
            Who We Are
          </div>

          <h2 className="max-w-5xl font-serif text-[2.65rem] leading-[1.05] text-[#181818] md:text-[5rem]">
            We Are Boutique.
            <span className="block italic text-[#4E6A5E]">
              Bespoke Workforce Design.
            </span>
            <span className="block">Exquisite Curation.</span>
            <span className="block italic text-[#4E6A5E]">
              Elite Concierge.
            </span>
          </h2>

          <div className="mt-14 max-w-4xl border-t border-black/10 pt-10">
            <div className="mb-6 text-xs uppercase tracking-[0.28em] text-[#4E6A5E]">
              Our Purpose
            </div>

            <p className="max-w-3xl text-[18px] italic leading-9 text-black/80 md:text-[22px]">
              We help organizations feel heard and understood, aligning workforce solutions to their goals, priorities, and available resources. We create simplified, flexible processes that strengthen business intelligence, customer experience, and employee experience — designed to deliver success.
            </p>

            <p className="mt-8 max-w-3xl font-serif text-[19px] leading-9 text-black/75 md:text-[21px]">
              Our workforce management experts bring over 20 years of experience strengthening processes, uncovering cost drivers, and creating practical, cost-effective solutions across diverse business environments.
            </p>
          </div>
        </div>
      </section>

      {/* CONSULTATION FORM */}
      <section id="schedule" className="scroll-mt-24 bg-[#F7F5F2] px-6 pb-24 pt-16">
        <div className="mx-auto grid max-w-6xl gap-14 md:grid-cols-[0.85fr_1.15fr] md:items-start">
          <div>
            <div className="mb-4 text-xs uppercase tracking-[0.32em] text-[#4E6A5E]">
              Complimentary Consultation
            </div>

            <h2 className="font-serif text-[2.55rem] leading-[1.05] md:text-[3.5rem]">
              We would love to hear from you.
            </h2>

            <p className="mt-6 max-w-lg text-[17px] leading-8 text-black/75">
              Questions, inquiries, or early ideas are welcome.
            </p>
          </div>

          <form
            name="consultation"
            method="POST"
            data-netlify="true"
            netlify-honeypot="bot-field"
            className="rounded-[28px] border border-black/10 bg-white p-8 shadow-sm md:p-10"
            onSubmit={() => setSubmitted(true)}
          >
            <input type="hidden" name="form-name" value="consultation" />

            <p className="hidden">
              <label>
                Don’t fill this out if you’re human:
                <input name="bot-field" />
              </label>
            </p>

            <input
              name="name"
              type="text"
              placeholder="Name"
              className="mb-4 w-full rounded-xl border p-4"
              required
            />

            <input
              name="title"
              type="text"
              placeholder="Title"
              className="mb-4 w-full rounded-xl border p-4"
            />

            <input
              name="company"
              type="text"
              placeholder="Company"
              className="mb-4 w-full rounded-xl border p-4"
            />

            <input
              name="email"
              type="email"
              placeholder="Email"
              className="mb-4 w-full rounded-xl border p-4"
              required
            />

            <input
              name="phone"
              type="tel"
              placeholder="Phone"
              className="mb-4 w-full rounded-xl border p-4"
            />

            <textarea
              name="message"
              placeholder="What would you like to explore?"
              className="mb-4 min-h-[150px] w-full rounded-xl border p-4"
              required
            />

            <button
              type="submit"
              className="w-full rounded-full bg-[#4E6A5E] px-4 py-4 text-center text-xs uppercase leading-5 tracking-[0.12em] text-white sm:text-sm"
            >
              Submit Request
            </button>

            {submitted && (
              <div className="mt-4 text-center text-black/75">
                Thank you. We’ll follow up thoughtfully.
              </div>
            )}

            <p className="mt-5 text-center text-[12px] leading-6 text-black/55">
              For information about how we handle personal information, please review our{' '}
              <a
                href="/privacy/"
                className="underline decoration-black/25 underline-offset-4 transition hover:text-[#4E6A5E]"
              >
                Privacy Policy
              </a>.
            </p>
          </form>
        </div>
      </section>


      {/* FOOTER */}
      <footer className="border-t border-black/10 bg-[#E9E4DA] px-6 py-16 text-[#181818] md:py-20">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-12 md:grid-cols-[1.6fr_1fr_1fr_1.2fr] md:gap-10">
            <div>
              <h2 className="font-serif text-[2.3rem] leading-none md:text-[2.8rem]">
                The Workforce<br />Planners
              </h2>
              <p className="mt-6 max-w-sm text-[15px] leading-7 text-black/65">
                Boutique workforce consulting for organizations seeking bespoke strategy,
                practical optimization, and concierge-level partnership.
              </p>
              <p className="mt-7 font-serif text-[18px] italic text-[#4E6A5E]">
                Workforce strategy, refined.
              </p>
            </div>

            <div>
              <div className="mb-5 text-xs uppercase tracking-[0.28em] text-[#4E6A5E]">
                Explore
              </div>
              <div className="space-y-3 text-[15px] text-black/65">
                <a href="#" className="block hover:text-[#4E6A5E]">Home</a>
                <a href="#seasonal-plan" className="block hover:text-[#4E6A5E]">
                  Seasonal / 2027 Workforce Management Plan
                </a>
                <a href="#solutions" className="block hover:text-[#4E6A5E]">Solutions</a>
                <a href="#schedule" className="block hover:text-[#4E6A5E]">Consultation</a>
                <a href="/privacy/" className="block hover:text-[#4E6A5E]">Privacy Policy</a>
              </div>
            </div>

            <div>
              <div className="mb-5 text-xs uppercase tracking-[0.28em] text-[#4E6A5E]">
                Services
              </div>
              <div className="space-y-3 text-[15px] text-black/65">
                <div>Forecasting</div>
                <div>Capacity Planning</div>
                <div>Scheduling</div>
                <div>Analytics</div>
              </div>
            </div>

            <div>
              <div className="mb-5 text-xs uppercase tracking-[0.28em] text-[#4E6A5E]">
                Contact
              </div>
              <p className="text-[15px] leading-7 text-black/65">
                Private consultation available by request.
              </p>

              <div className="mt-5 space-y-3 text-[15px] text-black/65">
                <a
                  href="mailto:info@theworkforceplanners.com"
                  className="block transition hover:text-[#4E6A5E]"
                >
                  info@theworkforceplanners.com
                </a>

                <a
                  href="https://www.linkedin.com/company/the-workforce-planners/"
                  target="_blank"
                  rel="noreferrer"
                  className="block transition hover:text-[#4E6A5E]"
                >
                  LinkedIn
                </a>
              </div>

              <a
                href="#schedule"
                className="mt-6 inline-flex rounded-full border border-[#4E6A5E] px-5 py-3 text-xs uppercase tracking-[0.14em] text-[#4E6A5E]"
              >
                Start a Conversation
              </a>
            </div>
          </div>

          <div className="mt-14 flex flex-col gap-4 border-t border-black/10 pt-7 text-[11px] uppercase tracking-[0.22em] text-black/45 md:flex-row md:items-center md:justify-between">
            <div>© 2026 The Workforce Planners</div>
            <div>Curate. Bespoke. Concierge.</div>
          </div>
        </div>
      </footer>

      <style>{`
        .logo-mark {
          font-family: "Times New Roman", Georgia, serif;
          font-weight: 400;
          text-shadow: 0 10px 24px rgba(0,0,0,0.45);
        }

        .logo-workforce,
        .logo-planners {
          font-weight: 400;
        }

        .logo-descriptor {
          font-family: Arial, Helvetica, sans-serif;
          font-weight: 500;
        }

        @keyframes wordOne {
          0% { opacity: 0; transform: translateY(8px); }
          10% { opacity: 1; transform: translateY(0); }
          82% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(0); }
        }

        @keyframes wordTwo {
          0%, 18% { opacity: 0; transform: translateY(8px); }
          30% { opacity: 1; transform: translateY(0); }
          82% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(0); }
        }

        @keyframes wordThree {
          0%, 36% { opacity: 0; transform: translateY(8px); }
          48% { opacity: 1; transform: translateY(0); }
          82% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(0); }
        }

        .animate-wordOne,
        .animate-wordTwo,
        .animate-wordThree {
          display: inline-block;
          opacity: 0;
          animation-duration: 5.8s;
          animation-timing-function: ease;
          animation-fill-mode: both;
          animation-iteration-count: infinite;
        }

        .animate-wordOne {
          animation-name: wordOne;
        }

        .animate-wordTwo {
          animation-name: wordTwo;
        }

        .animate-wordThree {
          animation-name: wordThree;
        }
      `}</style>
    </main>
  )
}